import { useCallback, useEffect, useRef, useState } from 'react'

/** How far the row slides to expose the action buttons behind it. */
export const REVEAL_WIDTH = 132

/** Movement before the gesture commits to an axis. Below this it could be a scroll. */
const AXIS_THRESHOLD = 10

/**
 * Swipe a row left to reveal actions behind it.
 *
 * The whole difficulty is that this lives inside a vertical scroller, so the
 * gesture has to decide which axis it belongs to before either behaviour can
 * start. It waits until the finger has moved AXIS_THRESHOLD, compares dx to
 * dy, and only then claims the gesture.
 *
 * `touch-action` is NOT used to suppress the scroll, and must not be: Safari
 * latches touch-action at pointerdown, so changing it once a drag is under way
 * does nothing at all (CLAUDE.md rule 13). Instead a non-passive `touchmove`
 * listener is registered from the outset and calls preventDefault() only while
 * a horizontal swipe is armed.
 */
export function useSwipeReveal() {
  const ref = useRef<HTMLDivElement>(null)
  const [offset, setOffset] = useState(0)
  const [revealed, setRevealed] = useState(false)

  const start = useRef<{ x: number; y: number; id: number } | null>(null)
  const axis = useRef<'undecided' | 'horizontal' | 'vertical'>('undecided')
  // Read by the touchmove listener, which is registered once and must not go
  // stale; a ref rather than state for exactly that reason.
  const armed = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const onTouchMove = (event: TouchEvent) => {
      if (armed.current) event.preventDefault()
    }

    el.addEventListener('touchmove', onTouchMove, { passive: false })
    return () => el.removeEventListener('touchmove', onTouchMove)
  }, [])

  const reset = useCallback(() => {
    setOffset(0)
    setRevealed(false)
    armed.current = false
  }, [])

  const onPointerDown = useCallback((event: React.PointerEvent) => {
    // A second finger during a swipe produces nonsense deltas. Ignore it.
    if (start.current) return
    start.current = { x: event.clientX, y: event.clientY, id: event.pointerId }
    axis.current = 'undecided'
  }, [])

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      const origin = start.current
      if (!origin || event.pointerId !== origin.id) return

      const dx = event.clientX - origin.x
      const dy = event.clientY - origin.y

      if (axis.current === 'undecided') {
        if (Math.abs(dx) < AXIS_THRESHOLD && Math.abs(dy) < AXIS_THRESHOLD) return
        axis.current = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical'
        armed.current = axis.current === 'horizontal'
      }

      if (axis.current !== 'horizontal') return

      const base = revealed ? -REVEAL_WIDTH : 0
      setOffset(Math.max(-REVEAL_WIDTH, Math.min(0, base + dx)))
    },
    [revealed],
  )

  const finish = useCallback(() => {
    if (axis.current === 'horizontal') {
      const open = offset < -REVEAL_WIDTH / 2
      setRevealed(open)
      setOffset(open ? -REVEAL_WIDTH : 0)
    }
    start.current = null
    axis.current = 'undecided'
    armed.current = false
  }, [offset])

  const onPointerCancel = useCallback(() => {
    start.current = null
    axis.current = 'undecided'
    armed.current = false
    setOffset(revealed ? -REVEAL_WIDTH : 0)
  }, [revealed])

  return {
    ref,
    offset,
    revealed,
    reset,
    /** True while a horizontal swipe is in progress, so the row can suppress its tap. */
    swiping: () => armed.current,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: finish,
      onPointerCancel,
    },
  }
}
